import { DataTable } from "@/components/shared";
import { getColumnsDocuments } from "../columns";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { Document } from "@/graphql/__generated__/graphql";
import { Dispatch, SetStateAction, useState } from "react";
import AddDocumentDialog from "./AddDocumentDialog";
import { CustomAlertDialog } from "../Dialog";
import { useAddDocumentToKnowledgeBaseMutationHook, useRemoveDocumentFromKnowledgeBaseMutationHook, useRemoveDocumentFromTenderMutationHook } from "@/hooks";
import { useParams } from "react-router-dom";
import { useTenderPermissionsData } from "@/hooks/cache";

interface DocumentsTableProps {
  documents: Document[];
  selectedDocument: Document | null;
  setSelectedDocument: Dispatch<SetStateAction<Document | null>>;
  loading: boolean;
  setMutationPollingActive: Dispatch<SetStateAction<boolean>>;
};

function DocumentsTable({ documents, selectedDocument, setSelectedDocument, loading, setMutationPollingActive }: DocumentsTableProps) {
  const { t } = useTranslation(["translation", "tables", "common"]);
  const intl = useIntl();
  const { tenderID } = useParams<{ tenderID: string }>();
  const { canManageDocument } = useTenderPermissionsData(Number(tenderID));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<
    "addToKnowledgeBase" | "removeFromKnowledgeBase" | "removeDocument" | null
  >(null);

  const { removeDocumentFromTenderMutation, loading: RemoveDocumentLoading } = useRemoveDocumentFromTenderMutationHook();
  const { AddDocumentToKnowledgeBase, loading: AddToKnowledgeLoading } = useAddDocumentToKnowledgeBaseMutationHook(setMutationPollingActive);
  const { RemoveDocumentFromKnowledgeBase, loading: RemoveFromKnowledgeLoading } = useRemoveDocumentFromKnowledgeBaseMutationHook(setMutationPollingActive);

  const isProcessing = RemoveDocumentLoading || AddToKnowledgeLoading || RemoveFromKnowledgeLoading;

  // Click handlers
  const handleOnClickRow = (document: Document) => {
    setSelectedDocument(document);
  };

  const handleOnClickRemove = () => {
    setDialogAction("removeDocument");
    setDialogOpen(true);
  };

  const handleOnClickAddToKnowledgeBase = () => {
    setDialogAction("addToKnowledgeBase");
    setDialogOpen(true);
  };

  const handleOnClickRemoveFromKnowledgeBase = () => {
    setDialogAction("removeFromKnowledgeBase");
    setDialogOpen(true);
  };

  // Functions
  const removeDocument = () => {
    removeDocumentFromTenderMutation({
      variables: {
        tenderId: Number(tenderID),
        documentId: selectedDocument?.id
      }
    }).then(() => {
      setSelectedDocument(null);
      setDialogOpen(false);
    });
  };

  const addToKnowledgeBase = () => {
    AddDocumentToKnowledgeBase({ variables: { documentId: selectedDocument?.id } }).then(() => {
      setDialogOpen(false);
    });
  };

  const removeFromKnowledgeBase = () => {
    RemoveDocumentFromKnowledgeBase({ variables: { documentId: selectedDocument?.id } }).then(() => {
      setDialogOpen(false);
    });
  };

  const downloadDocument = () => {
    window.open(selectedDocument?.url, "_blank");
  };

  const dialogContent = {
    removeDocument: {
      title: t("edittender.documents.dialogs.remove.title"),
      body: <><span className="break-all">{selectedDocument?.fileName}:</span> {t("edittender.documents.dialogs.remove.description")}</>,
      actionFn: removeDocument,
    },
    addToKnowledgeBase: {
      title:t("edittender.documents.dialogs.addtoknowledge.title"),
      body: <><span className="break-all">{selectedDocument?.fileName}:</span> {t("edittender.documents.dialogs.addtoknowledge.description")}</>,
      actionFn: addToKnowledgeBase,
    },
    removeFromKnowledgeBase: {
      title: t("edittender.documents.dialogs.removefromknowledge.title"),
      body: <><span className="break-all">{selectedDocument?.fileName}:</span> {t("edittender.documents.dialogs.removefromknowledge.description")}</>,
      actionFn: removeFromKnowledgeBase,
    },
    downloadDocument: {
      title: t("edittender.documents.dialogs.download.title"),
      body: <><span className="break-all">{selectedDocument?.fileName}:</span> {t("edittender.documents.dialogs.download.description")}</>,
      actionFn: downloadDocument,
    }
  };

  const currentDialog = dialogAction ? dialogContent[dialogAction] : null;

  return (
    <div className="flex-col-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h1 className="font-semibold">{ t("edittender.documents.title") }</h1>
        {
          canManageDocument && (
            <AddDocumentDialog tenderId={Number(tenderID)} setMutationPollingActive={setMutationPollingActive} />
          )
        }
      </div>
      <DataTable
        disableRowColour={true}
        loading={loading}
        displayAmount={5} // Default to 5 rows
        data={documents as Document[] ?? []}
        clickableRows={true}
        onRowClick={handleOnClickRow}
        selectableRows={true}
        preselectedRowId="0"
        columns={getColumnsDocuments(
          intl,
          t,
          canManageDocument,
          handleOnClickRemove,
          handleOnClickAddToKnowledgeBase,
          handleOnClickRemoveFromKnowledgeBase,
          downloadDocument,
          isProcessing || loading,
        )}
        className="max-h-[calc(100vh-10.75rem)]"
      />

      {currentDialog && (
        <CustomAlertDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          title={currentDialog.title}
          body={currentDialog.body}
          actionButtonFn={currentDialog.actionFn}
          loading={isProcessing}
        />
      )}
    </div>
  );
}

export default DocumentsTable;
